import { useState, useRef, useCallback } from "react";
import html2canvas from "html2canvas";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LifeBuoy, Camera, Upload, X, Loader2 } from "lucide-react";

/**
 * Topbar feedback widget.
 *
 * One simple flow: a "Share feedback" icon button in the topbar opens a dialog
 * with a text area, a category dropdown, and two ways to attach a screenshot:
 *  1. "Capture current screen" → html2canvas of <body> (no browser perms)
 *  2. "Upload image" → file input
 *
 * The dialog hides itself momentarily during capture so the screenshot doesn't
 * include the open dialog over the page being reported.
 *
 * Posts multipart to POST /api/tickets, then drops a success toast with the
 * link to "My tickets".
 */
const CATEGORIES = [
    { value: "bug", label: "Bug" },
    { value: "feature_request", label: "Feature request" },
    { value: "question", label: "Question" },
    { value: "billing", label: "Billing" },
    { value: "other", label: "Other" },
];

export default function FeedbackWidget() {
    const [open, setOpen] = useState(false);
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("other");
    const [imageBlob, setImageBlob] = useState(null);  // Blob or File
    const [imageUrl, setImageUrl] = useState("");
    const [capturing, setCapturing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef(null);

    const reset = useCallback(() => {
        setDescription("");
        setCategory("other");
        setImageBlob(null);
        if (imageUrl) URL.revokeObjectURL(imageUrl);
        setImageUrl("");
        if (fileRef.current) fileRef.current.value = "";
    }, [imageUrl]);

    const captureScreen = useCallback(async () => {
        setCapturing(true);
        // Hide the dialog momentarily so the screenshot captures the actual
        // page being reported, not our modal overlay.
        setOpen(false);
        await new Promise((r) => setTimeout(r, 250));
        try {
            const canvas = await html2canvas(document.body, {
                useCORS: true,
                allowTaint: true,
                logging: false,
                // Keep file size sane — downscale by 0.75x on large screens
                scale: Math.min(1, 1280 / Math.max(window.innerWidth, 1)),
                backgroundColor: "#ffffff",
            });
            const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.85));
            if (!blob) throw new Error("Canvas conversion failed");
            if (imageUrl) URL.revokeObjectURL(imageUrl);
            const url = URL.createObjectURL(blob);
            setImageBlob(new File([blob], "screen.png", { type: "image/png" }));
            setImageUrl(url);
            toast.success("Screenshot attached");
        } catch (err) {
            toast.error(`Capture failed: ${err.message || err}`);
        } finally {
            setCapturing(false);
            setOpen(true);
        }
    }, [imageUrl]);

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please pick an image file (PNG / JPEG / WEBP)");
            return;
        }
        if (f.size > 3 * 1024 * 1024) {
            toast.error("Image too large (max 3 MB)");
            return;
        }
        if (imageUrl) URL.revokeObjectURL(imageUrl);
        setImageBlob(f);
        setImageUrl(URL.createObjectURL(f));
    };

    const onSubmit = async () => {
        if (!description.trim()) {
            toast.error("Please describe what's wrong or what you'd like.");
            return;
        }
        setSubmitting(true);
        const fd = new FormData();
        fd.append("description", description.trim());
        fd.append("category", category);
        fd.append("page_url", window.location.href);
        if (imageBlob) fd.append("screenshot", imageBlob, imageBlob.name || "screenshot.png");
        try {
            await api.post("/tickets", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Thanks! Your ticket is in — track it in My tickets.");
            setOpen(false);
            reset();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 border border-border bg-secondary/40 hover:bg-secondary text-[11px] font-mono text-foreground/70"
                title="Share feedback / report an issue"
                data-testid="feedback-widget-btn"
            >
                <LifeBuoy className="w-3.5 h-3.5" />
                <span>Feedback</span>
            </button>
            {/* Mobile compact variant */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="md:hidden p-2 hover:bg-secondary"
                title="Share feedback"
                aria-label="Share feedback"
                data-testid="feedback-widget-btn-mobile"
            >
                <LifeBuoy className="w-5 h-5" />
            </button>

            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
                <DialogContent className="max-w-lg" data-testid="feedback-widget-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <LifeBuoy className="w-4 h-4 text-ikb" />
                            Share feedback
                        </DialogTitle>
                        <DialogDescription>
                            Tell us what's working, what's broken, or what you'd like to see. A screenshot helps a lot.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Category</label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full mt-1 border border-border px-2 py-1.5 text-sm bg-white"
                                data-testid="feedback-category"
                            >
                                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Your feedback</label>
                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={5}
                                placeholder="What happened? What did you expect?"
                                className="mt-1"
                                maxLength={8000}
                                data-testid="feedback-description"
                            />
                            <div className="text-[10px] text-foreground/50 text-right mt-0.5">{description.length}/8000</div>
                        </div>

                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-1 block">Screenshot (optional)</label>
                            <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                    type="button"
                                    onClick={captureScreen}
                                    disabled={capturing || submitting}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-none"
                                    data-testid="feedback-capture-screen"
                                >
                                    {capturing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
                                    Capture current screen
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    disabled={submitting}
                                    variant="outline"
                                    size="sm"
                                    className="rounded-none"
                                    data-testid="feedback-upload-image"
                                >
                                    <Upload className="w-3.5 h-3.5 mr-1" />
                                    Upload image
                                </Button>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={onPickFile}
                                    className="hidden"
                                    data-testid="feedback-file-input"
                                />
                                {imageUrl ? (
                                    <button
                                        type="button"
                                        onClick={() => { if (imageUrl) URL.revokeObjectURL(imageUrl); setImageBlob(null); setImageUrl(""); if (fileRef.current) fileRef.current.value = ""; }}
                                        className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1"
                                        data-testid="feedback-remove-image"
                                    >
                                        <X className="w-3 h-3" /> Remove
                                    </button>
                                ) : null}
                            </div>
                            {imageUrl ? (
                                <div className="mt-2 border border-border overflow-hidden">
                                    <img src={imageUrl} alt="Attached screenshot preview" className="max-h-48 w-full object-contain bg-secondary/30" data-testid="feedback-image-preview" />
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => { setOpen(false); reset(); }}
                            disabled={submitting}
                            data-testid="feedback-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={onSubmit}
                            disabled={submitting || !description.trim()}
                            className="bg-ikb hover:bg-[#001f7a] text-white"
                            data-testid="feedback-submit"
                        >
                            {submitting ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sending…</> : "Send feedback"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
